<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <div class="orangehrm-ter-header">
        <oxd-text tag="h6" class="orangehrm-main-title">
          TER Schedules
        </oxd-text>
        <div
          v-if="hasLastUpdatedInfo"
          class="orangehrm-ter-last-updated"
          role="status"
        >
          <template v-if="lastUpdatedByUserName">
            <span class="orangehrm-ter-last-updated__label"
              >Last updated by</span
            >
            <span class="orangehrm-ter-last-updated__user">{{
              lastUpdatedByUserName
            }}</span>
            <span class="orangehrm-ter-last-updated__time"
              >({{ lastUpdatedAt }})</span
            >
          </template>
          <template v-else>
            <span class="orangehrm-ter-last-updated__label">Last updated</span>
            <span class="orangehrm-ter-last-updated__time"
              >({{ lastUpdatedAt }})</span
            >
          </template>
        </div>
      </div>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.name"
                label="Schedule Name"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.taxYear"
                label="Tax Year"
                :rules="rules.taxYear"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="form.effectiveFrom"
                label="Effective From"
                :rules="rules.date"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="form.effectiveTo"
                label="Effective To"
                :rules="rules.date"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text
                class="orangehrm-main-title orangehrm-ter-section-heading"
              >
                Monthly TER
              </oxd-text>
              <div class="orangehrm-ter-mapping">
                <oxd-text class="orangehrm-ter-mapping-title">
                  PTKP to TER Group Mapping
                </oxd-text>
                <div class="orangehrm-ter-mapping-toolbar">
                  <input
                    v-model="newGroupName"
                    type="text"
                    class="orangehrm-ter-mapping-input"
                    placeholder="New TER group name (e.g. D)"
                  />
                  <oxd-button
                    display-type="secondary"
                    label="Add Group"
                    class="orangehrm-ter-mapping-add-btn"
                    @click="addTerGroup"
                  />
                </div>
                <table class="orangehrm-ter-mapping-table">
                  <thead>
                    <tr>
                      <th>TER Group</th>
                      <th>PTKP Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="group in terGroups" :key="`mapping-${group}`">
                      <td>{{ group }}</td>
                      <td>
                        <div class="orangehrm-ter-mapping-row">
                          <input
                            v-model="form.ptkpMappingInput[group]"
                            type="text"
                            class="orangehrm-ter-mapping-input"
                            placeholder="Comma separated, e.g. TK/0, TK/1"
                          />
                          <oxd-icon-button
                            name="trash"
                            class="orangehrm-ter-remove-btn"
                            @click="deleteTerGroup(group)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="orangehrm-ter-groups-grid">
                <div
                  v-for="group in terGroups"
                  :key="group"
                  class="orangehrm-ter-group"
                >
                  <div class="orangehrm-ter-group-header">
                    <oxd-text class="orangehrm-ter-group-title">
                      Group {{ group }}
                    </oxd-text>
                  </div>
                  <div class="orangehrm-ter-table-wrap">
                    <table class="orangehrm-ter-table">
                      <thead>
                        <tr>
                          <th>Lower Bound</th>
                          <th>Upper Bound</th>
                          <th>Rate (%)</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, index) in form.terSchedule[group]"
                          :key="`${group}-${index}`"
                        >
                          <td>
                            <input
                              :value="formatLocaleNumber(row.lowerBound)"
                              type="text"
                              inputmode="decimal"
                              class="orangehrm-ter-input"
                              @input="onNumericInput($event, row, 'lowerBound')"
                            />
                          </td>
                          <td>
                            <input
                              :value="
                                row.upperBound === null
                                  ? ''
                                  : formatLocaleNumber(row.upperBound)
                              "
                              type="text"
                              inputmode="decimal"
                              class="orangehrm-ter-input"
                              placeholder="Leave blank for ∞"
                              @input="onNumericInput($event, row, 'upperBound')"
                            />
                          </td>
                          <td>
                            <input
                              :value="formatLocaleNumber(row.ratePercent)"
                              type="text"
                              inputmode="decimal"
                              class="orangehrm-ter-input"
                              @input="
                                onNumericInput($event, row, 'ratePercent')
                              "
                            />
                          </td>
                          <td class="orangehrm-ter-actions">
                            <oxd-icon-button
                              name="trash"
                              class="orangehrm-ter-remove-btn"
                              @click="removeTerRow(group, index)"
                            />
                          </td>
                        </tr>
                        <tr v-if="form.terSchedule[group].length === 0">
                          <td colspan="4" class="orangehrm-ter-empty">
                            No rows yet
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colspan="4" class="orangehrm-ter-table-footer">
                            <div class="orangehrm-ter-table-footer__actions">
                              <oxd-button
                                display-type="ghost"
                                label="Clear"
                                class="orangehrm-ter-footer-btn"
                                :disabled="form.terSchedule[group].length === 0"
                                @click="onClickClearTerGroup(group)"
                              />
                              <oxd-button
                                display-type="secondary"
                                label="+ Add Row"
                                class="orangehrm-ter-footer-btn"
                                @click="addTerRow(group)"
                              />
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-text
                class="orangehrm-main-title orangehrm-ter-section-heading orangehrm-ter-section-heading--daily"
              >
                Daily TER
              </oxd-text>
              <div class="orangehrm-ter-group">
                <div class="orangehrm-ter-group-header">
                  <oxd-text class="orangehrm-ter-group-title">
                    Daily TER Rows
                  </oxd-text>
                </div>
                <div class="orangehrm-ter-table-wrap">
                  <table class="orangehrm-ter-table">
                    <thead>
                      <tr>
                        <th>Lower Bound</th>
                        <th>Upper Bound</th>
                        <th>Rate (%)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, index) in form.dailyTerSchedule"
                        :key="`daily-${index}`"
                      >
                        <td>
                          <input
                            :value="formatLocaleNumber(row.lowerBound)"
                            type="text"
                            inputmode="decimal"
                            class="orangehrm-ter-input"
                            @input="onNumericInput($event, row, 'lowerBound')"
                          />
                        </td>
                        <td>
                          <input
                            :value="
                              row.upperBound === null
                                ? ''
                                : formatLocaleNumber(row.upperBound)
                            "
                            type="text"
                            inputmode="decimal"
                            class="orangehrm-ter-input"
                            placeholder="Leave blank for ∞"
                            @input="onNumericInput($event, row, 'upperBound')"
                          />
                        </td>
                        <td>
                          <input
                            :value="formatLocaleNumber(row.ratePercent)"
                            type="text"
                            inputmode="decimal"
                            class="orangehrm-ter-input"
                            @input="onNumericInput($event, row, 'ratePercent')"
                          />
                        </td>
                        <td class="orangehrm-ter-actions">
                          <oxd-icon-button
                            name="trash"
                            class="orangehrm-ter-remove-btn"
                            @click="removeDailyTerRow(index)"
                          />
                        </td>
                      </tr>
                      <tr v-if="form.dailyTerSchedule.length === 0">
                        <td colspan="4" class="orangehrm-ter-empty">
                          No daily rows yet
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="4" class="orangehrm-ter-table-footer">
                          <div class="orangehrm-ter-table-footer__actions">
                            <oxd-button
                              display-type="ghost"
                              label="Clear"
                              class="orangehrm-ter-footer-btn"
                              :disabled="form.dailyTerSchedule.length === 0"
                              @click="onClickClearDailyTerRows"
                            />
                            <oxd-button
                              display-type="secondary"
                              label="+ Add Row"
                              class="orangehrm-ter-footer-btn"
                              @click="addDailyTerRow"
                            />
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
      <confirmation-dialog
        ref="clearTerRowsDialog"
        :title="clearTerRowsDialogTitle"
        :subtitle="clearTerRowsDialogSubtitle"
        cancel-label="Cancel"
        confirm-label="Clear"
        confirm-button-type="secondary"
      />
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import ConfirmationDialog from '@ohrm/components/dialogs/ConfirmationDialog';
import {
  required,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';

const DEFAULT_TER_MONTHLY_A = [
  {lowerBound: 0, upperBound: 5400000, ratePercent: 0},
  {lowerBound: 5400001, upperBound: 5650000, ratePercent: 0.25},
  {lowerBound: 5650001, upperBound: 5950000, ratePercent: 0.5},
  {lowerBound: 5950001, upperBound: 6300000, ratePercent: 0.75},
  {lowerBound: 6300001, upperBound: 6750000, ratePercent: 1},
  {lowerBound: 6750001, upperBound: 7500000, ratePercent: 1.25},
  {lowerBound: 7500001, upperBound: 8550000, ratePercent: 1.5},
  {lowerBound: 8550001, upperBound: 9650000, ratePercent: 1.75},
  {lowerBound: 9650001, upperBound: 10050000, ratePercent: 2},
  {lowerBound: 10050001, upperBound: 10350000, ratePercent: 2.25},
  {lowerBound: 10350001, upperBound: 10700000, ratePercent: 2.5},
  {lowerBound: 10700001, upperBound: 11050000, ratePercent: 3},
  {lowerBound: 11050001, upperBound: 11600000, ratePercent: 3.5},
  {lowerBound: 11600001, upperBound: 12500000, ratePercent: 4},
  {lowerBound: 12500001, upperBound: 13750000, ratePercent: 5},
  {lowerBound: 13750001, upperBound: 15100000, ratePercent: 6},
  {lowerBound: 15100001, upperBound: 16950000, ratePercent: 7},
  {lowerBound: 16950001, upperBound: 19750000, ratePercent: 8},
  {lowerBound: 19750001, upperBound: 24150000, ratePercent: 9},
  {lowerBound: 24150001, upperBound: 26450000, ratePercent: 10},
  {lowerBound: 26450001, upperBound: 28000000, ratePercent: 11},
  {lowerBound: 28000001, upperBound: 30050000, ratePercent: 12},
  {lowerBound: 30050001, upperBound: 32400000, ratePercent: 13},
  {lowerBound: 32400001, upperBound: 35400000, ratePercent: 14},
  {lowerBound: 35400001, upperBound: 39100000, ratePercent: 15},
  {lowerBound: 39100001, upperBound: 43850000, ratePercent: 16},
  {lowerBound: 43850001, upperBound: 47800000, ratePercent: 17},
  {lowerBound: 47800001, upperBound: 51400000, ratePercent: 18},
  {lowerBound: 51400001, upperBound: 56300000, ratePercent: 19},
  {lowerBound: 56300001, upperBound: 62200000, ratePercent: 20},
  {lowerBound: 62200001, upperBound: 68600000, ratePercent: 21},
  {lowerBound: 68600001, upperBound: 77500000, ratePercent: 22},
  {lowerBound: 77500001, upperBound: 89000000, ratePercent: 23},
  {lowerBound: 89000001, upperBound: 103000000, ratePercent: 24},
  {lowerBound: 103000001, upperBound: 125000000, ratePercent: 25},
  {lowerBound: 125000001, upperBound: 157000000, ratePercent: 26},
  {lowerBound: 157000001, upperBound: 206000000, ratePercent: 27},
  {lowerBound: 206000001, upperBound: 337000000, ratePercent: 28},
  {lowerBound: 337000001, upperBound: 454000000, ratePercent: 29},
  {lowerBound: 454000001, upperBound: 550000000, ratePercent: 30},
  {lowerBound: 550000001, upperBound: 695000000, ratePercent: 31},
  {lowerBound: 695000001, upperBound: 910000000, ratePercent: 32},
  {lowerBound: 910000001, upperBound: 1400000000, ratePercent: 33},
  {lowerBound: 1400000001, upperBound: null, ratePercent: 34},
];

const DEFAULT_TER_MONTHLY_B = [
  {lowerBound: 0, upperBound: 6200000, ratePercent: 0},
  {lowerBound: 6200001, upperBound: 6500000, ratePercent: 0.25},
  {lowerBound: 6500001, upperBound: 6850000, ratePercent: 0.5},
  {lowerBound: 6850001, upperBound: 7300000, ratePercent: 0.75},
  {lowerBound: 7300001, upperBound: 9200000, ratePercent: 1},
  {lowerBound: 9200001, upperBound: 10750000, ratePercent: 1.5},
  {lowerBound: 10750001, upperBound: 11250000, ratePercent: 2},
  {lowerBound: 11250001, upperBound: 11600000, ratePercent: 2.5},
  {lowerBound: 11600001, upperBound: 12600000, ratePercent: 3},
  {lowerBound: 12600001, upperBound: 13600000, ratePercent: 4},
  {lowerBound: 13600001, upperBound: 14950000, ratePercent: 5},
  {lowerBound: 14950001, upperBound: 16400000, ratePercent: 6},
  {lowerBound: 16400001, upperBound: 18450000, ratePercent: 7},
  {lowerBound: 18450001, upperBound: 21850000, ratePercent: 8},
  {lowerBound: 21850001, upperBound: 26000000, ratePercent: 9},
  {lowerBound: 26000001, upperBound: 27700000, ratePercent: 10},
  {lowerBound: 27700001, upperBound: 29350000, ratePercent: 11},
  {lowerBound: 29350001, upperBound: 31450000, ratePercent: 12},
  {lowerBound: 31450001, upperBound: 33950000, ratePercent: 13},
  {lowerBound: 33950001, upperBound: 37100000, ratePercent: 14},
  {lowerBound: 37100001, upperBound: 41100000, ratePercent: 15},
  {lowerBound: 41100001, upperBound: 45800000, ratePercent: 16},
  {lowerBound: 45800001, upperBound: 49500000, ratePercent: 17},
  {lowerBound: 49500001, upperBound: 53800000, ratePercent: 18},
  {lowerBound: 53800001, upperBound: 58500000, ratePercent: 19},
  {lowerBound: 58500001, upperBound: 64000000, ratePercent: 20},
  {lowerBound: 64000001, upperBound: 71000000, ratePercent: 21},
  {lowerBound: 71000001, upperBound: 80000000, ratePercent: 22},
  {lowerBound: 80000001, upperBound: 93000000, ratePercent: 23},
  {lowerBound: 93000001, upperBound: 109000000, ratePercent: 24},
  {lowerBound: 109000001, upperBound: 129000000, ratePercent: 25},
  {lowerBound: 129000001, upperBound: 163000000, ratePercent: 26},
  {lowerBound: 163000001, upperBound: 211000000, ratePercent: 27},
  {lowerBound: 211000001, upperBound: 374000000, ratePercent: 28},
  {lowerBound: 374000001, upperBound: 459000000, ratePercent: 29},
  {lowerBound: 459000001, upperBound: 555000000, ratePercent: 30},
  {lowerBound: 555000001, upperBound: 704000000, ratePercent: 31},
  {lowerBound: 704000001, upperBound: 957000000, ratePercent: 32},
  {lowerBound: 957000001, upperBound: 1405000000, ratePercent: 33},
  {lowerBound: 1405000001, upperBound: null, ratePercent: 34},
];

const DEFAULT_TER_MONTHLY_C = [
  {lowerBound: 0, upperBound: 6600000, ratePercent: 0},
  {lowerBound: 6600001, upperBound: 6950000, ratePercent: 0.25},
  {lowerBound: 6950001, upperBound: 7350000, ratePercent: 0.5},
  {lowerBound: 7350001, upperBound: 7800000, ratePercent: 0.75},
  {lowerBound: 7800001, upperBound: 8850000, ratePercent: 1},
  {lowerBound: 8850001, upperBound: 9800000, ratePercent: 1.25},
  {lowerBound: 9800001, upperBound: 10950000, ratePercent: 1.5},
  {lowerBound: 10950001, upperBound: 11200000, ratePercent: 1.75},
  {lowerBound: 11200001, upperBound: 12050000, ratePercent: 2},
  {lowerBound: 12050001, upperBound: 12950000, ratePercent: 3},
  {lowerBound: 12950001, upperBound: 14150000, ratePercent: 4},
  {lowerBound: 14150001, upperBound: 15550000, ratePercent: 5},
  {lowerBound: 15550001, upperBound: 17050000, ratePercent: 6},
  {lowerBound: 17050001, upperBound: 19500000, ratePercent: 7},
  {lowerBound: 19500001, upperBound: 22700000, ratePercent: 8},
  {lowerBound: 22700001, upperBound: 26600000, ratePercent: 9},
  {lowerBound: 26600001, upperBound: 28100000, ratePercent: 10},
  {lowerBound: 28100001, upperBound: 30100000, ratePercent: 11},
  {lowerBound: 30100001, upperBound: 32600000, ratePercent: 12},
  {lowerBound: 32600001, upperBound: 35400000, ratePercent: 13},
  {lowerBound: 35400001, upperBound: 38900000, ratePercent: 14},
  {lowerBound: 38900001, upperBound: 43000000, ratePercent: 15},
  {lowerBound: 43000001, upperBound: 47400000, ratePercent: 16},
  {lowerBound: 47400001, upperBound: 51200000, ratePercent: 17},
  {lowerBound: 51200001, upperBound: 55800000, ratePercent: 18},
  {lowerBound: 55800001, upperBound: 60400000, ratePercent: 19},
  {lowerBound: 60400001, upperBound: 66700000, ratePercent: 20},
  {lowerBound: 66700001, upperBound: 74500000, ratePercent: 21},
  {lowerBound: 74500001, upperBound: 83200000, ratePercent: 22},
  {lowerBound: 83200001, upperBound: 95600000, ratePercent: 23},
  {lowerBound: 95600001, upperBound: 110000000, ratePercent: 24},
  {lowerBound: 110000001, upperBound: 134000000, ratePercent: 25},
  {lowerBound: 134000001, upperBound: 169000000, ratePercent: 26},
  {lowerBound: 169000001, upperBound: 221000000, ratePercent: 27},
  {lowerBound: 221000001, upperBound: 390000000, ratePercent: 28},
  {lowerBound: 390000001, upperBound: 463000000, ratePercent: 29},
  {lowerBound: 463000001, upperBound: 561000000, ratePercent: 30},
  {lowerBound: 561000001, upperBound: 709000000, ratePercent: 31},
  {lowerBound: 709000001, upperBound: 965000000, ratePercent: 32},
  {lowerBound: 965000001, upperBound: 1419000000, ratePercent: 33},
  {lowerBound: 1419000001, upperBound: null, ratePercent: 34},
];

const DEFAULT_TER_DAILY = [
  {lowerBound: 0, upperBound: 450000, ratePercent: 0},
  {lowerBound: 450001, upperBound: 2500000, ratePercent: 0.5},
];

const model = {
  id: null,
  name: 'TER Schedule',
  taxYear: String(new Date().getFullYear()),
  effectiveFrom: `${new Date().getFullYear()}-01-01`,
  effectiveTo: `${new Date().getFullYear()}-12-31`,
  terSchedule: {
    A: DEFAULT_TER_MONTHLY_A.map((row) => ({...row})),
    B: DEFAULT_TER_MONTHLY_B.map((row) => ({...row})),
    C: DEFAULT_TER_MONTHLY_C.map((row) => ({...row})),
  },
  dailyTerSchedule: DEFAULT_TER_DAILY.map((row) => ({...row})),
  ptkpMapping: {
    A: ['TK/0', 'TK/1', 'K/0'],
    B: ['TK/2', 'TK/3', 'K/1', 'K/2'],
    C: ['K/3'],
  },
  ptkpMappingInput: {
    A: 'TK/0, TK/1, K/0',
    B: 'TK/2, TK/3, K/1, K/2',
    C: 'K/3',
  },
};

const DEFAULT_PTKP_MAPPING = {
  A: ['TK/0', 'TK/1', 'K/0'],
  B: ['TK/2', 'TK/3', 'K/1', 'K/2'],
  C: ['K/3'],
};

const toNumberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeRow = (row = {}) => ({
  lowerBound: toNumberOrZero(row.lowerBound ?? row.lower_bound),
  upperBound:
    row.upperBound === null || row.upper_bound === null || row.upperBound === ''
      ? null
      : toNumberOrZero(row.upperBound ?? row.upper_bound),
  ratePercent: toNumberOrZero(row.ratePercent ?? row.rate_percent),
});

const resolveGroupsFromSchedule = (schedule = {}) => {
  const groups = Object.keys(schedule || {}).filter(
    (key) => key !== 'ptkpMapping' && key !== 'daily',
  );
  if (groups.length === 0) {
    return ['A', 'B', 'C'];
  }
  return groups;
};

const normalizeSchedule = (schedule = {}, groups = ['A', 'B', 'C']) =>
  groups.reduce((acc, group) => {
    const rawRows = Array.isArray(schedule[group]) ? schedule[group] : [];
    acc[group] = rawRows.map((row) => normalizeRow(row));
    return acc;
  }, {});

const normalizePtkpMapping = (mapping = {}, groups = ['A', 'B', 'C']) =>
  groups.reduce((acc, group) => {
    const statuses = Array.isArray(mapping[group]) ? mapping[group] : [];
    const normalized = statuses
      .map((status) =>
        String(status || '')
          .trim()
          .toUpperCase(),
      )
      .filter((status) => status.length > 0);
    if (normalized.length === 0 && Array.isArray(DEFAULT_PTKP_MAPPING[group])) {
      acc[group] = [...DEFAULT_PTKP_MAPPING[group]];
      return acc;
    }
    acc[group] = normalized;
    return acc;
  }, {});

const ptkpMappingToInput = (mapping = {}, groups = ['A', 'B', 'C']) =>
  groups.reduce((acc, group) => {
    const statuses = Array.isArray(mapping[group]) ? mapping[group] : [];
    acc[group] = statuses.join(', ');
    return acc;
  }, {});

const ptkpInputToMapping = (input = {}, groups = ['A', 'B', 'C']) =>
  groups.reduce((acc, group) => {
    const raw = String(input[group] ?? '');
    acc[group] = raw
      .split(',')
      .map((status) => status.trim().toUpperCase())
      .filter((status) => status.length > 0);
    return acc;
  }, {});

export default {
  components: {
    'confirmation-dialog': ConfirmationDialog,
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/ter-schedules',
    );
    return {
      http,
    };
  },
  data() {
    return {
      isLoading: false,
      form: {...model},
      rules: {
        name: [required, shouldNotExceedCharLength(100)],
        taxYear: [required],
        date: [required],
      },
      lastUpdatedAt: null,
      lastUpdatedByUserName: null,
      newGroupName: '',
      clearTerRowsDialogTitle: '',
      clearTerRowsDialogSubtitle: '',
    };
  },
  computed: {
    terGroups() {
      return Object.keys(this.form.terSchedule);
    },
    hasLastUpdatedInfo() {
      return Boolean(this.lastUpdatedAt);
    },
  },
  created() {
    this.isLoading = true;
    this.http
      .getAll()
      .then((response) => {
        const data = response?.data?.data ?? [];
        if (!data.length) {
          return;
        }
        const latest = data[0];
        this.form.id = latest.id;
        this.form.name = latest.name;
        this.form.taxYear = String(latest.taxYear);
        this.form.effectiveFrom = latest.effectiveFrom;
        this.form.effectiveTo = latest.effectiveTo;
        const groups = resolveGroupsFromSchedule(latest.schedule ?? {});
        this.form.terSchedule = normalizeSchedule(
          latest.schedule ?? {},
          groups,
        );
        this.form.dailyTerSchedule = Array.isArray(latest.schedule?.daily)
          ? latest.schedule.daily.map((row) => normalizeRow(row))
          : [];
        this.form.ptkpMapping = normalizePtkpMapping(
          latest.schedule?.ptkpMapping ?? model.ptkpMapping,
          groups,
        );
        this.form.ptkpMappingInput = ptkpMappingToInput(
          this.form.ptkpMapping,
          groups,
        );
        this.lastUpdatedAt = latest.updatedAt ?? null;
        this.lastUpdatedByUserName = latest.updatedByUserName ?? null;
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    getLocaleDecimalSeparator() {
      const parts = new Intl.NumberFormat().formatToParts(1.1);
      return parts.find((part) => part.type === 'decimal')?.value ?? '.';
    },
    getLocaleGroupSeparator() {
      const parts = new Intl.NumberFormat().formatToParts(1000);
      return parts.find((part) => part.type === 'group')?.value ?? ',';
    },
    formatLocaleNumber(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '';
      }
      return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      }).format(number);
    },
    parseLocaleNumber(raw) {
      const value = String(raw ?? '').trim();
      if (!value) {
        return null;
      }
      const decimal = this.getLocaleDecimalSeparator();
      const group = this.getLocaleGroupSeparator();
      const escapedGroup = group.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const normalized = value
        .replace(new RegExp(escapedGroup, 'g'), '')
        .replace(/\s/g, '')
        .replace(decimal, '.')
        .replace(/[^\d.-]/g, '');
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    },
    onNumericInput(event, row, field) {
      const parsed = this.parseLocaleNumber(event?.target?.value);
      if (field === 'upperBound') {
        row[field] = parsed;
        return;
      }
      row[field] = parsed ?? 0;
    },
    addTerRow(group) {
      this.form.terSchedule[group].push({
        lowerBound: 0,
        upperBound: null,
        ratePercent: 0,
      });
    },
    onClickClearTerGroup(group) {
      this.clearTerRowsDialogTitle = 'Clear all rows?';
      this.clearTerRowsDialogSubtitle = `Every bracket row in Group ${group} will be removed. Reload the page without saving to discard other edits.`;
      this.$refs.clearTerRowsDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.form.terSchedule[group] = [];
        }
      });
    },
    onClickClearDailyTerRows() {
      this.clearTerRowsDialogTitle = 'Clear all daily rows?';
      this.clearTerRowsDialogSubtitle =
        'Every row in the Daily TER table will be removed. Reload the page without saving to discard other edits.';
      this.$refs.clearTerRowsDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.form.dailyTerSchedule = [];
        }
      });
    },
    removeTerRow(group, index) {
      this.form.terSchedule[group].splice(index, 1);
    },
    addDailyTerRow() {
      this.form.dailyTerSchedule.push({
        lowerBound: 0,
        upperBound: null,
        ratePercent: 0,
      });
    },
    removeDailyTerRow(index) {
      this.form.dailyTerSchedule.splice(index, 1);
    },
    addTerGroup() {
      const group = String(this.newGroupName ?? '')
        .trim()
        .toUpperCase();
      if (!group) {
        return;
      }
      if (this.form.terSchedule[group]) {
        this.$toast.warn({
          title: this.$t('general.warning'),
          message: `TER group ${group} already exists`,
        });
        return;
      }
      this.form.terSchedule[group] = [];
      this.form.ptkpMapping[group] = [];
      this.form.ptkpMappingInput[group] = '';
      this.newGroupName = '';
    },
    deleteTerGroup(group) {
      const hasTerRows = (this.form.terSchedule[group] ?? []).length > 0;
      if (hasTerRows) {
        this.$toast.warn({
          title: this.$t('general.warning'),
          message: `Cannot delete TER group ${group} because it already has bracket rows`,
        });
        return;
      }
      delete this.form.terSchedule[group];
      delete this.form.ptkpMapping[group];
      delete this.form.ptkpMappingInput[group];
    },
    validateRangeRows(rows, label) {
      if (!Array.isArray(rows) || rows.length === 0) {
        return null;
      }
      const normalized = rows.map((row, index) => {
        const lower = Number(row?.lowerBound ?? row?.lower_bound ?? 0);
        const upperRaw = row?.upperBound ?? row?.upper_bound ?? null;
        const upper =
          upperRaw === null || upperRaw === '' ? null : Number(upperRaw);
        return {index, lower, upper};
      });

      for (const row of normalized) {
        if (!Number.isFinite(row.lower) || row.lower < 0) {
          return `${label}: row ${row.index + 1} has invalid lower bound`;
        }
        if (
          row.upper !== null &&
          (!Number.isFinite(row.upper) || row.upper < 0)
        ) {
          return `${label}: row ${row.index + 1} has invalid upper bound`;
        }
        if (row.upper !== null && row.lower > row.upper) {
          return `${label}: row ${
            row.index + 1
          } lower bound cannot be greater than upper bound`;
        }
      }

      const sorted = [...normalized].sort((a, b) => a.lower - b.lower);
      for (let i = 0; i < sorted.length; i++) {
        const current = sorted[i];
        if (current.upper === null && i !== sorted.length - 1) {
          return `${label}: open-ended bracket must be the last range`;
        }
        for (let j = i + 1; j < sorted.length; j++) {
          const next = sorted[j];
          const currentUpper =
            current.upper === null ? Number.POSITIVE_INFINITY : current.upper;
          const nextUpper =
            next.upper === null ? Number.POSITIVE_INFINITY : next.upper;
          const overlaps =
            current.lower <= nextUpper && next.lower <= currentUpper;
          if (overlaps) {
            return `${label}: ranges overlap near ${current.lower} - ${
              current.upper === null ? '∞' : current.upper
            } and ${next.lower} - ${next.upper === null ? '∞' : next.upper}`;
          }
          if (currentUpper < next.lower) {
            break;
          }
        }
      }
      return null;
    },
    validateTerBeforeSave(schedulePayload) {
      const groupKeys = Object.keys(schedulePayload).filter(
        (key) => key !== 'ptkpMapping' && key !== 'daily',
      );
      for (const group of groupKeys) {
        const message = this.validateRangeRows(
          schedulePayload[group] ?? [],
          `Monthly TER Group ${group}`,
        );
        if (message) {
          return message;
        }
      }
      return this.validateRangeRows(schedulePayload.daily ?? [], 'Daily TER');
    },
    onSave() {
      const groups = this.terGroups;
      this.form.ptkpMapping = ptkpInputToMapping(
        this.form.ptkpMappingInput,
        groups,
      );
      const schedule = {
        ...normalizeSchedule(this.form.terSchedule, groups),
        daily: this.form.dailyTerSchedule.map((row) => normalizeRow(row)),
        ptkpMapping: normalizePtkpMapping(this.form.ptkpMapping, groups),
      };
      const validationMessage = this.validateTerBeforeSave(schedule);
      if (validationMessage) {
        this.$toast.warn({
          title: this.$t('general.warning'),
          message: validationMessage,
        });
        return;
      }

      this.isLoading = true;
      const payload = {
        name: this.form.name,
        taxYear: parseInt(this.form.taxYear, 10),
        effectiveFrom: this.form.effectiveFrom,
        effectiveTo: this.form.effectiveTo,
        schedule,
      };
      const action = this.form.id
        ? this.http.update(this.form.id, payload)
        : this.http.create(payload);
      action
        .then((response) => {
          const saved = response?.data?.data ?? null;
          if (saved?.id) {
            this.form.id = saved.id;
          }
          this.lastUpdatedAt = saved?.updatedAt ?? this.lastUpdatedAt;
          this.lastUpdatedByUserName =
            saved?.updatedByUserName ?? this.lastUpdatedByUserName;
          return this.$toast.saveSuccess();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.orangehrm-ter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.orangehrm-ter-last-updated {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem 0.5rem;
  max-width: 100%;
  padding: 0.55rem 0.85rem;
  border-radius: 8px;
  background: linear-gradient(135deg, #f6f8fc 0%, #eef2f9 100%);
  border: 1px solid #e1e8f2;
  box-shadow: 0 1px 2px rgba(38, 52, 77, 0.04);
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #526887;
}

.orangehrm-ter-last-updated__label {
  font-weight: 500;
  color: #64728c;
  letter-spacing: 0.01em;
}

.orangehrm-ter-last-updated__user {
  font-weight: 600;
  color: #26344d;
}

.orangehrm-ter-last-updated__time {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.8em;
  font-weight: 500;
  color: #5a6f8e;
  white-space: nowrap;
}

.orangehrm-ter-section-heading {
  padding-top: 0.6rem;
  margin-bottom: 0.8rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid #e3e8f2;
}

.orangehrm-ter-section-heading--daily {
  padding-top: 1.2rem;
}

.orangehrm-ter-mapping {
  border: 1px solid #e8ecf3;
  border-radius: 12px;
  background: #fff;
  padding: 0.9rem 1rem;
  margin-bottom: 0.5rem;
}

.orangehrm-ter-mapping-title {
  font-weight: 600;
  color: #42536d;
  margin-bottom: 0.5rem;
}

.orangehrm-ter-mapping-toolbar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
}

.orangehrm-ter-mapping-table {
  width: 100%;
  border-collapse: collapse;
}

.orangehrm-ter-mapping-table th,
.orangehrm-ter-mapping-table td {
  border: 1px solid #edf1f7;
  padding: 0.65rem;
  font-family: sans-serif;
  box-sizing: border-box;
  vertical-align: middle;
}

.orangehrm-ter-mapping-table th {
  background: #fcfdff;
  color: #526887;
  font-weight: 600;
  text-align: center;
}

.orangehrm-ter-mapping-table td {
  background: #fff;
  color: #26344d;
}

.orangehrm-ter-mapping-input {
  width: 100%;
  border: 1px solid #d6deea;
  border-radius: 6px;
  padding: 0.45rem 0.6rem;
  font-size: 0.9rem;
  color: #26344d;
}
.orangehrm-ter-mapping-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.orangehrm-ter-mapping-add-btn {
  min-width: 68px;
}

.orangehrm-ter-group {
  margin-bottom: 1rem;
  border: 1px solid #e8ecf3;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  width: 100%;
}

.orangehrm-ter-groups-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  width: 100%;
}

@media (min-width: 1200px) {
  .orangehrm-ter-groups-grid {
    grid-template-columns: 1fr 1fr;
  }

  .orangehrm-ter-group {
    max-width: 100%;
    margin-bottom: 0;
  }
}

.orangehrm-ter-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  background: #f9fbff;
  border-bottom: 1px solid #edf1f7;
}

.orangehrm-ter-group-title {
  font-weight: 600;
  color: #5a6f8e;
}

.orangehrm-ter-table-footer {
  padding: 0.65rem 0.75rem;
  background: #fafbfd;
  vertical-align: middle;
}

.orangehrm-ter-table-footer__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.orangehrm-ter-footer-btn {
  min-width: 100px;
}

.orangehrm-ter-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.orangehrm-ter-table-wrap {
  overflow-x: hidden;
}

.orangehrm-ter-table th,
.orangehrm-ter-table td {
  border: 1px solid #edf1f7;
  padding: 0.65rem;
  box-sizing: border-box;
  vertical-align: middle;
  overflow: hidden;
}

.orangehrm-ter-table th {
  background: #fcfdff;
  color: #526887;
  font-weight: 600;
  text-align: center;
  font-family: sans-serif;
}

.orangehrm-ter-table th:nth-child(4),
.orangehrm-ter-table td:nth-child(4) {
  width: 58px;
}

.orangehrm-ter-table td {
  background: #fff;
}

.orangehrm-ter-table tfoot td {
  background: #fafbfd;
}

.orangehrm-ter-input {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0;
  font-size: 0.95rem;
  color: #26344d;
  background: #fff;
}

.orangehrm-ter-input:focus {
  outline: none;
  box-shadow: none;
}

.orangehrm-ter-actions {
  text-align: center;
}

.orangehrm-ter-remove-btn {
  color: #d86c5b !important;
}

.orangehrm-ter-empty {
  text-align: center;
  color: #64728c;
  padding: 1rem 0.5rem;
  font-style: italic;
}
</style>
